import { T, Num } from 'gt-react';
import { LightningIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTaskAutomationStats } from 'src/api/rpc/hooks/tasks/useTaskAutomation';

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

function RateChip({
  label,
  value,
  testId,
}: {
  label: ReactNode;
  value: string;
  testId: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-full bg-stone-50 px-2 py-0.5 text-stone-600">
      {label}
      <span
        className="font-semibold tabular-nums text-stone-800"
        data-testid={testId}
      >
        {value}
      </span>
    </span>
  );
}

/**
 * Aggregate automation stats for the `/tasks` queue page, shown when a single
 * type filter is active: how the type's recently closed tasks scored on
 * autonomy / intervention and how many ran fully hands-off. Renders nothing
 * until stats arrive or when the type has no closed tasks yet.
 */
export function TaskAutomationStatStrip({
  taskTypeKey,
}: {
  taskTypeKey: string;
}) {
  const { data: stats } = useTaskAutomationStats(taskTypeKey);
  if (!stats || stats.taskCount === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs"
      data-testid="task-automation-stat-strip"
    >
      <span className="inline-flex items-center gap-1 text-stone-500">
        <LightningIcon className="size-3.5" />
        <T context="heading for the aggregate task automation stats strip">
          Automation
        </T>
      </span>
      <span
        className="text-stone-500"
        data-testid="automation-stats-task-count"
      >
        {stats.truncated ? (
          <T context="minimum count of closed tasks when automation stats are truncated">
            <Num>{stats.taskCount}</Num>+ closed
          </T>
        ) : (
          <T context="count of closed tasks the automation stats aggregate over">
            <Num>{stats.taskCount}</Num> closed
          </T>
        )}
      </span>
      <RateChip
        label={
          <T context="share of checklist steps closed without a human">
            Autonomy
          </T>
        }
        value={formatRate(stats.meanAutonomyRate)}
        testId="automation-stats-autonomy"
      />
      <RateChip
        label={
          <T context="share of automated steps that needed a human anyway">
            Intervention
          </T>
        }
        value={formatRate(stats.meanInterventionRate)}
        testId="automation-stats-intervention"
      />
      <span
        className="text-stone-500"
        data-testid="automation-stats-fully-autonomous"
      >
        <T context="count of tasks whose checklist ran without any human">
          <Num>{stats.fullyAutonomousCount}</Num> hands-off
        </T>
      </span>
    </div>
  );
}
